import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Button,
  Card,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Collapse,
  UncontrolledTooltip,
  FormText,
} from "reactstrap";
import BackIcon from "react-icons/lib/ti/arrow-back";
import DownloadIcon from "react-icons/lib/ti/download";
import EditIcon from "react-icons/lib/md/edit";
import DeleteIcon from "react-icons/lib/md/delete";
import CheckIcon from "react-icons/lib/md/check-circle";
import ViewHeader from "../view-header";
import PageEmptyContent from "../../utils/page.empty-content";
import { appHelpers } from "../../../_helpers";
import { apiActions, alertActions } from "../../../_actions";
import { exchangeRateService } from "../../../_services";
import { appConstants } from "../../../_constants";

const ViewContent = ({ children }) => (
  <div className="view-content view-components">{children}</div>
);

const emptyForm = {
  Country: "",
  CurrencyCode: "",
  NGNExchangeRate: "",
  USDExchangeRate: "",
  USD20_ROE: "",
};

/**
 * Parse the API error strings array into a map of:
 *   { [rowNumber]: { fieldErrors: { FieldName: "message" }, rawMessages: ["..."] } }
 *
 * Example input: ["Row 2: CurrencyCode is required.", "Row 3: USDExchangeRate is not a valid decimal."]
 */
function parseApiErrors(errors) {
  if (!Array.isArray(errors)) return {};

  var result = {};

  errors.forEach(function (msg) {
    // Match "Row N: FieldName rest of message"
    var match = String(msg).match(/^Row\s+(\d+):\s+(\w+)\s+(.+)$/i);
    if (!match) return;

    var rowNum = parseInt(match[1], 10);
    var fieldName = match[2];
    var reason = match[3].replace(/\.$/, ""); // strip trailing period

    if (!result[rowNum]) {
      result[rowNum] = { fieldErrors: {}, rawMessages: [] };
    }
    result[rowNum].fieldErrors[fieldName] = fieldName + " " + reason;
    result[rowNum].rawMessages.push(msg);
  });

  return result;
}

/**
 * Build error records by pairing each parsed error row with the original
 * uploaded records (rows are 1-indexed in the CSV, row 1 = header,
 * so row 2 = records[0], row 3 = records[1], etc.)
 */
function buildErrorRecords(parsedErrors, originalRecords) {
  var errorRecords = [];

  Object.keys(parsedErrors).forEach(function (rowNumStr) {
    var rowNum = parseInt(rowNumStr, 10);
    // CSV row 2 → originalRecords[0]; subtract 2 for header offset
    var recordIndex = rowNum - 2;
    var baseRecord = (originalRecords && originalRecords[recordIndex]) || {};
    var info = parsedErrors[rowNum];

    errorRecords.push(
      Object.assign({}, baseRecord, {
        _rowNumber: rowNum,
        _fieldErrors: info.fieldErrors,
        // Keep a human-readable combined error string for the Error column
        Error: info.rawMessages.join("; "),
      }),
    );
  });

  return errorRecords;
}

class BulkUploadExchangeRateDetails extends Component {
  constructor(props) {
    super(props);

    const uploadState =
      props.location && props.location.state ? props.location.state : {};

    const rawUploadResult = uploadState.uploadResult || uploadState || {};
    // Original records passed alongside the upload result so we can
    // reconstruct error rows. Set this when navigating to this page.
    const originalRecords = uploadState.originalRecords || [];
    // detect backend string-only errors (e.g. ["Row 2: CurrencyCode is required."])
    const stringErrors =
      rawUploadResult &&
      rawUploadResult.Source &&
      Array.isArray(rawUploadResult.Source.Errors) &&
      rawUploadResult.Source.Errors.length > 0 &&
      typeof rawUploadResult.Source.Errors[0] === "string"
        ? rawUploadResult.Source.Errors
        : null;

    this.state = {
      readyRecords: this.getReadyRecords(rawUploadResult),
      errorRecords: this.getErrorRecords(rawUploadResult, originalRecords),
      originalRecords: originalRecords,
      stringErrors: stringErrors,
      errorsOpen: false,
      editModalOpen: false,
      editMode: "ready",
      editIndex: null,
      editForm: emptyForm,
      editFormErrors: {},
      confirmed: false,
      confirmedRecords: null,
      hasChanges: false,
      successModalOpen: false,
      totalRateSent: 0,
    };

    this.openEditModal = this.openEditModal.bind(this);
    this.closeEditModal = this.closeEditModal.bind(this);
    this.handleEditChange = this.handleEditChange.bind(this);
    this.saveEdit = this.saveEdit.bind(this);
    this.deleteRecord = this.deleteRecord.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.handleUpload = this.handleUpload.bind(this);
    this.handleSuccessDone = this.handleSuccessDone.bind(this);
    this.exportErrorCsv = this.exportErrorCsv.bind(this);
    this.toggleErrors = this.toggleErrors.bind(this);
    this.handleBack = this.handleBack.bind(this);
  }

  // Render a read-only list of string errors. If originalRecords are
  // available we'll show a preview of the corresponding CSV row.
  renderStringErrors(errors, originalRecords) {
    var self = this;
    if (!errors || !errors.length) return null;

    return (
      <div>
        <ul className="mb-3" style={{ lineHeight: 1.8 }}>
          {errors.map(function (msg, idx) {
            var m = String(msg).match(/^Row\s+(\d+):\s*(.+)$/i);
            var rowNum = m ? parseInt(m[1], 10) : null;
            var text = m ? m[2] : msg;
            var recordIndex = rowNum ? rowNum - 2 : null; // CSV row2 -> originalRecords[0]
            var original = recordIndex !== null && originalRecords && originalRecords[recordIndex]
              ? originalRecords[recordIndex]
              : null;

            return (
              <li key={idx} className="mb-2">
                <div>
                  <strong>{rowNum ? `Row ${rowNum}: ` : ""}</strong>
                  <span>{text}</span>
                </div>
                {original && (
                  <div className="mt-1 small text-muted" style={{ fontSize: 13 }}>
                    <span className="mr-3">{original.Country || "-"}</span>
                    <span className="mr-3">{original.CurrencyCode || "-"}</span>
                    <span className="mr-3">NGN: {original.NGNExchangeRate || "-"}</span>
                    <span>USD: {original.USDExchangeRate || "-"}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="text-muted" style={{ fontSize: 13 }}>
          These are validation errors returned by the server. Check your document, fix and reupload.
        </div>
      </div>
    );
  }

  getReadyRecords(uploadResult) {
    if (!uploadResult) return [];
    if (
      uploadResult.Source &&
      Array.isArray(uploadResult.Source.SuccessfulRecords)
    ) {
      return uploadResult.Source.SuccessfulRecords;
    }
    if (Array.isArray(uploadResult.SuccessfulRecords))
      return uploadResult.SuccessfulRecords;
    if (Array.isArray(uploadResult.Source)) return uploadResult.Source;
    if (Array.isArray(uploadResult.Results)) return uploadResult.Results;
    if (uploadResult.data) {
      if (Array.isArray(uploadResult.data.Source))
        return uploadResult.data.Source;
      if (Array.isArray(uploadResult.data.Results))
        return uploadResult.data.Results;
      if (Array.isArray(uploadResult.data.SuccessfulRecords))
        return uploadResult.data.SuccessfulRecords;
    }
    return [];
  }

  getErrorRecords(uploadResult, originalRecords) {
    if (!uploadResult) return [];

    // New API shape — Errors is an array of strings
    if (
      uploadResult.Source &&
      Array.isArray(uploadResult.Source.Errors) &&
      uploadResult.Source.Errors.length > 0 &&
      typeof uploadResult.Source.Errors[0] === "string"
    ) {
      var parsed = parseApiErrors(uploadResult.Source.Errors);
      return buildErrorRecords(parsed, originalRecords);
    }

    // Errors array of objects (older shape)
    if (
      uploadResult.Source &&
      Array.isArray(uploadResult.Source.Errors) &&
      uploadResult.Source.Errors.length > 0 &&
      typeof uploadResult.Source.Errors[0] === "object"
    ) {
      return uploadResult.Source.Errors;
    }

    if (Array.isArray(uploadResult.Errors)) return uploadResult.Errors;
    if (Array.isArray(uploadResult.ErrorRecords))
      return uploadResult.ErrorRecords;
    if (Array.isArray(uploadResult.InvalidRecords))
      return uploadResult.InvalidRecords;
    if (Array.isArray(uploadResult.FailedRecords))
      return uploadResult.FailedRecords;
    if (uploadResult.data) {
      if (Array.isArray(uploadResult.data.Errors))
        return uploadResult.data.Errors;
      if (Array.isArray(uploadResult.data.ErrorRecords))
        return uploadResult.data.ErrorRecords;
      if (Array.isArray(uploadResult.data.InvalidRecords))
        return uploadResult.data.InvalidRecords;
      if (Array.isArray(uploadResult.data.FailedRecords))
        return uploadResult.data.FailedRecords;
    }
    return [];
  }

  formatRate(value) {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toLocaleString();
  }

  getRecordError(record) {
    return (
      record.Error || record.ErrorMessage || record.ValidationMessage || ""
    );
  }

  /**
   * Return the per-field error map for a record.
   * For records built from the new string-error API, _fieldErrors is already set.
   * For older object-shaped records, fall back to checking empty/zero values.
   */
  getFieldErrors(record) {
    if (record._fieldErrors) return record._fieldErrors;

    // Fallback: derive from blank / zero values
    var errors = {};
    if (!record.CurrencyCode || !String(record.CurrencyCode).trim())
      errors.CurrencyCode = "Currency code is missing";
    if (
      record.NGNExchangeRate === "" ||
      record.NGNExchangeRate === null ||
      record.NGNExchangeRate === undefined ||
      isNaN(Number(record.NGNExchangeRate))
    )
      errors.NGNExchangeRate = "Exchange rate (NGN) is missing or invalid";
    if (
      record.USDExchangeRate === "" ||
      record.USDExchangeRate === null ||
      record.USDExchangeRate === undefined ||
      isNaN(Number(record.USDExchangeRate))
    )
      errors.USDExchangeRate = "Exchange rate (USD) is missing or invalid";
    if (
      record.USD20_ROE === "" ||
      record.USD20_ROE === null ||
      record.USD20_ROE === undefined ||
      isNaN(Number(record.USD20_ROE))
    )
      errors.USD20_ROE = "USD equivalent of $20 is missing or invalid";
    return errors;
  }

  toggleErrors() {
    this.setState(function (prev) {
      return { errorsOpen: !prev.errorsOpen };
    });
  }

  openEditModal(record, index, mode) {
    var form = {
      Country: record.Country || "",
      CurrencyCode: record.CurrencyCode || "",
      NGNExchangeRate: record.NGNExchangeRate || "",
      USDExchangeRate: record.USDExchangeRate || "",
      USD20_ROE: record.USD20_ROE || "",
    };
    var editFormErrors = mode === "error" ? this.getFieldErrors(record) : {};

    this.setState({
      editModalOpen: true,
      editMode: mode,
      editIndex: index,
      editForm: form,
      editFormErrors: editFormErrors,
    });
  }

  closeEditModal() {
    this.setState({
      editModalOpen: false,
      editMode: "ready",
      editIndex: null,
      editForm: emptyForm,
      editFormErrors: {},
    });
  }

  handleEditChange(event) {
    var name = event.target.name;
    var value = event.target.value;

    this.setState(function (prev) {
      var updatedForm = Object.assign({}, prev.editForm, { [name]: value });
      // Auto-update USD20_ROE when USDExchangeRate changes
      if (name === "USDExchangeRate") {
        var usdNum = Number(value);
        updatedForm.USD20_ROE = !isNaN(usdNum) && value !== "" ? usdNum * 20 : "";
      }
      // Re-derive field errors in real time for error-mode records
      var editFormErrors =
        prev.editMode === "error"
          ? prev.editFormErrors[name] // only clear the field being edited
            ? (function () {
                var errs = Object.assign({}, prev.editFormErrors);
                // Clear error for this field if it now has a value
                if (value && value.trim && value.trim()) {
                  delete errs[name];
                } else if (!isNaN(Number(value)) && value !== "") {
                  delete errs[name];
                }
                return errs;
              })()
            : prev.editFormErrors
          : {};
      return { editForm: updatedForm, editFormErrors: editFormErrors };
    });
  }

  saveEdit() {
    var editMode = this.state.editMode;
    var editIndex = this.state.editIndex;
    var editForm = this.state.editForm;

    if (editMode === "error") {
      // Re-validate using the field-errors logic
      var remainingErrors = {};
      if (!editForm.CurrencyCode || !String(editForm.CurrencyCode).trim()) {
        remainingErrors.CurrencyCode = "Currency code is missing";
      }
      if (
        editForm.NGNExchangeRate === "" ||
        isNaN(Number(editForm.NGNExchangeRate))
      ) {
        remainingErrors.NGNExchangeRate =
          "Exchange rate (NGN) is missing or invalid";
      }
      if (
        editForm.USDExchangeRate === "" ||
        isNaN(Number(editForm.USDExchangeRate))
      ) {
        remainingErrors.USDExchangeRate =
          "Exchange rate (USD) is missing or invalid";
      }
      if (editForm.USD20_ROE === "" || isNaN(Number(editForm.USD20_ROE))) {
        remainingErrors.USD20_ROE =
          "USD equivalent of $20 is missing or invalid";
      }

      if (Object.keys(remainingErrors).length > 0) {
        this.setState({ editFormErrors: remainingErrors });
        return;
      }

      // All errors fixed → promote to readyRecords
      this.setState(function (prev) {
        var updatedRecord = Object.assign(
          {},
          prev.errorRecords[editIndex],
          editForm,
          {
            NGNExchangeRate: Number(editForm.NGNExchangeRate),
            USDExchangeRate: Number(editForm.USDExchangeRate),
            USD20_ROE: Number(editForm.USD20_ROE),
          },
        );
        // Strip internal error tracking fields
        delete updatedRecord._rowNumber;
        delete updatedRecord._fieldErrors;
        delete updatedRecord.Error;
        delete updatedRecord.ErrorMessage;
        delete updatedRecord.ValidationMessage;

        return {
          errorRecords: prev.errorRecords.filter(function (_, i) {
            return i !== editIndex;
          }),
          readyRecords: prev.readyRecords.concat([updatedRecord]),
          editModalOpen: false,
          editMode: "ready",
          editIndex: null,
          editForm: emptyForm,
          editFormErrors: {},
          hasChanges: true,
        };
      });
      return;
    }

    // Normal ready-record save
    this.setState(function (prev) {
      var records = prev.readyRecords.slice();
      records[editIndex] = Object.assign({}, records[editIndex], editForm, {
        NGNExchangeRate: Number(editForm.NGNExchangeRate),
        USDExchangeRate: Number(editForm.USDExchangeRate),
        USD20_ROE: Number(editForm.USD20_ROE),
      });
      return {
        readyRecords: records,
        editModalOpen: false,
        editMode: "ready",
        editIndex: null,
        editForm: emptyForm,
        editFormErrors: {},
        hasChanges: true,
      };
    });
  }

  deleteRecord(index, mode) {
    var listKey = mode === "error" ? "errorRecords" : "readyRecords";
    this.setState(function (prev) {
      return {
        [listKey]: prev[listKey].filter(function (_, i) {
          return i !== index;
        }),
        hasChanges: true,
      };
    });
  }

  handleConfirm() {
    this.setState({
      confirmed: true,
      confirmedRecords: this.state.readyRecords.slice(),
    });
  }

  handleBack() {
    this.setState({ confirmed: false, confirmedRecords: null });
  }

  handleUpload() {
    var self = this;
    var confirmedRecords = this.state.confirmedRecords;
    const { dispatch } = this.props;
    var activeUser = appHelpers.getActiveUser();
    var createdBy = activeUser
      ? activeUser.userName || activeUser.UserName || ""
      : "";

    if (!confirmedRecords || !confirmedRecords.length) return;

    dispatch(apiActions.startRequest());

    var payload = confirmedRecords.map(function (record) {
      return {
        Id: record.Id || 0,
        Country: record.Country,
        CurrencyCode: record.CurrencyCode,
        NGNExchangeRate: Number(record.NGNExchangeRate),
        USDExchangeRate: Number(record.USDExchangeRate),
        USD20_ROE: Number(record.USD20_ROE),
        CreatedBy: createdBy,
      };
    });

    exchangeRateService
      .saveBulkRate(payload)
      .then(function (res) {
        if (res.status === appConstants.SUCCESS_RESPONSE) {
          self.setState({
            successModalOpen: true,
            totalRateSent: confirmedRecords.length,
          });
        } else if (res.status === appConstants.ERROR_RESPONSE) {
          var errorMessage = appHelpers.interpretErrorResponseAlt2(
            res.response && res.response.Message
              ? res.response.Message
              : res.response,
          );
          self.notify(errorMessage, appConstants.NOTIFY_ERROR);
          dispatch(alertActions.error(errorMessage));
        }
        dispatch(apiActions.endRequest());
      })
      .catch(function (err) {
        var errorMessage = appHelpers.interpretErrorResponseAlt2(err);
        self.notify(errorMessage, appConstants.NOTIFY_ERROR);
        dispatch(alertActions.error(errorMessage));
        dispatch(apiActions.endRequest());
      });
  }

  handleSuccessDone() {
    this.setState({ successModalOpen: false });
    this.props.history.push("/exchange-rate");
  }

  exportErrorCsv() {
    var self = this;
    var errorRecords = this.state.errorRecords;
    // If the backend returned string-only errors and we have originalRecords,
    // build an annotated CSV with the original row columns plus an Error column.
    if (this.state.stringErrors && this.state.stringErrors.length) {
      var errors = this.state.stringErrors;
      var originals = this.state.originalRecords || [];

      // Determine headers from first original record if available
      var baseHeaders =
        originals && originals.length && typeof originals[0] === "object"
          ? Object.keys(originals[0])
          : ["Row"];

      var headers = baseHeaders.concat(["Error"]);

      var csvBody = errors
        .map(function (msg) {
          var m = String(msg).match(/^Row\s+(\d+):\s*(.+)$/i);
          var rowNum = m ? parseInt(m[1], 10) : null;
          var text = m ? m[2] : msg;
          var recordIndex = rowNum ? rowNum - 2 : null;
          var original = recordIndex !== null && originals[recordIndex] ? originals[recordIndex] : null;

          if (original) {
            return headers
              .map(function (h) {
                if (h === "Error") return '"' + String(text).replace(/"/g, '""') + '"';
                return '"' + String(original[h] || "").replace(/"/g, '""') + '"';
              })
              .join(",");
          }

          // fallback: Row,Error
          return '"' + (rowNum || "") + '","' + String(text).replace(/"/g, '""') + '"';
        })
        .join("\n");

      var csvRows = [headers.join(","), csvBody];
      var blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      var url = window.URL.createObjectURL(blob);
      var link = document.createElement("a");
      link.href = url;
      link.download = "exchange-rate-errors.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      return;
    }

    // Default: export errorRecords built previously (object-shaped records)
    var headers = [
      "Country",
      "CurrencyCode",
      "NGNExchangeRate",
      "USDExchangeRate",
      "USD20_ROE",
      "Error",
    ];
    var csvRows = [
      headers.join(","),
      errorRecords
        .map(function (record) {
          return headers
            .map(function (header) {
              var value = header === "Error" ? self.getRecordError(record) : record[header];
              return '"' + String(value || "").replace(/"/g, '""') + '"';
            })
            .join(",");
        })
        .join("\n"),
    ];
    var blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    var url = window.URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = url;
    link.download = "exchange-rate-errors.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  }

  renderTable(records, mode) {
    var self = this;
    var isError = mode === "error";

    if (!records.length) {
      return <PageEmptyContent message="No record ready to upload!" />;
    }

    return (
      <div className="table-responsive">
        <table className="table exchange-rate-upload-table mb-0">
          <thead>
            <tr>
              <th>Country Name</th>
              <th>Currency Code</th>
              <th>Exchange Rate (NGN)</th>
              <th>Exchange Rate (USD)</th>
              <th>USD Equivalent of $20</th>
              {isError && <th>Error</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {records.map(function (record, index) {
              var fieldErrors = isError ? self.getFieldErrors(record) : {};

              return (
                <tr
                  key={index}
                  style={isError ? { background: "#fff7f0" } : null}
                >
                  <td>{record.Country || "—"}</td>

                  {/* Orange if CurrencyCode is the problem field */}
                  <td
                    style={
                      fieldErrors.CurrencyCode
                        ? { color: "#ff7a1a", fontWeight: 500 }
                        : { color: "#6c757d" }
                    }
                  >
                    {record.CurrencyCode || "---"}
                  </td>

                  {/* Orange if NGNExchangeRate is the problem field */}
                  <td
                    style={
                      fieldErrors.NGNExchangeRate ? { color: "#ff7a1a" } : null
                    }
                  >
                    {record.NGNExchangeRate !== undefined &&
                    record.NGNExchangeRate !== ""
                      ? self.formatRate(record.NGNExchangeRate)
                      : "---"}
                  </td>

                  {/* Orange if USDExchangeRate is the problem field */}
                  <td
                    style={
                      fieldErrors.USDExchangeRate ? { color: "#ff7a1a" } : null
                    }
                  >
                    {record.USDExchangeRate !== undefined &&
                    record.USDExchangeRate !== ""
                      ? self.formatRate(record.USDExchangeRate)
                      : "---"}
                  </td>

                  {/* Orange if USD20_ROE is the problem field */}
                  <td
                    style={fieldErrors.USD20_ROE ? { color: "#ff7a1a" } : null}
                  >
                    {record.USD20_ROE !== undefined && record.USD20_ROE !== ""
                      ? self.formatRate(record.USD20_ROE)
                      : "---"}
                  </td>

                  {isError && (
                    <td className="text-danger" style={{ fontSize: 13 }}>
                      {self.getRecordError(record)}
                    </td>
                  )}

                  <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                    <button
                      id={`delete-btn-${mode}-${index}`}
                      type="button"
                      className="btn btn-link p-0 mr-3 text-muted"
                      onClick={function () {
                        self.deleteRecord(index, mode);
                      }}
                    >
                      <DeleteIcon size={18} />
                    </button>
                    <UncontrolledTooltip target={`delete-btn-${mode}-${index}`}>
                      {isError
                        ? "Delete removes this record from the list. The action cannot be undone"
                        : "Delete removes this record from the list. The action cannot be undone"}
                    </UncontrolledTooltip>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted"
                      onClick={function () {
                        self.openEditModal(record, index, mode);
                      }}
                    >
                      <EditIcon size={18} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderSummaryTable(records) {
    var self = this;
    if (!records || !records.length) {
      return <PageEmptyContent message="No record ready to upload!" />;
    }
    return (
      <div className="table-responsive">
        <table className="table exchange-rate-upload-table mb-0">
          <thead>
            <tr>
              <th>Country Name</th>
              <th>Currency Code</th>
              <th>Exchange Rate (NGN)</th>
              <th>Exchange Rate (USD)</th>
              <th>USD Equivalent of $20</th>
            </tr>
          </thead>
          <tbody>
            {records.map(function (record, index) {
              return (
                <tr key={index}>
                  <td>{record.Country}</td>
                  <td className="text-muted">{record.CurrencyCode}</td>
                  <td>{self.formatRate(record.NGNExchangeRate)}</td>
                  <td>{self.formatRate(record.USDExchangeRate)}</td>
                  <td>{self.formatRate(record.USD20_ROE)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    var state = this.state;
    var readyRecords = state.readyRecords;
    var errorRecords = state.errorRecords;
    var errorsOpen = state.errorsOpen;
    var editModalOpen = state.editModalOpen;
    var editForm = state.editForm || {};
    var editFormErrors = state.editFormErrors;
    var editMode = state.editMode;
    var confirmed = state.confirmed;
    var confirmedRecords = state.confirmedRecords;
    var successModalOpen = state.successModalOpen;
    var totalRateSent = state.totalRateSent;
    var totalCount = readyRecords.length + errorRecords.length;
    var errorFieldCount = Object.keys(editFormErrors).length;

    // Show USD equivalent of $20 in the edit modal when USDExchangeRate is present
    var usdRate = Number(editForm.USDExchangeRate);
    var showUSDEquivalent =
      editForm.USDExchangeRate !== undefined &&
      editForm.USDExchangeRate !== null &&
      editForm.USDExchangeRate !== "" &&
      !isNaN(usdRate);
    var formattedUSDEq = (usdRate * 20).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });

    return (
      <div className="view exchange-rate-view">
        <ViewHeader />
        <ViewContent>
          <Card
            className="p-4 bg-white"
            style={{ minHeight: "calc(100vh - 120px)", paddingBottom: "8rem", position: "relative" }}
          >
            <div
              className="d-flex align-items-center mb-5"
              onClick={function () {
                this.props.history.push("/exchange-rate/bulk-upload");
              }.bind(this)}
              style={{ cursor: "pointer" }}
            >
              <BackIcon size={24} className="mr-2" />
              <h5 className="mb-0">Bulk Upload Exchange Rate</h5>
            </div>

            <div className="text-center mb-5">
              <h1 className="font-weight-bold mb-3" style={{ fontSize: 26 }}>
                {confirmed
                  ? "Upload summary"
                  : "We found " + totalCount + " exchange rates to upload"}
              </h1>
              <p className="text-muted mb-0" style={{ fontSize: 17 }}>
                {confirmed
                  ? "Here is a summary of rates that will be added to the system"
                  : errorRecords.length
                    ? "We found errors in the CSV file preventing our system from identifying some rates"
                    : "We found no errors in the CSV file, please review the data, confirm and continue."}
              </p>
            </div>

            {/* Error banner */}
            {!confirmed && errorRecords.length > 0 && (
              <div
                className="d-flex align-items-center justify-content-between p-3 mb-5"
                style={{ background: "#fff7ef", borderRadius: 14 }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: "#ffeadb",
                      color: "#ff7a1a",
                      fontSize: 24,
                    }}
                  >
                    !
                  </div>
                  
                  <div>
                    <h4 className="mb-1">
                      {errorRecords.length} exchange rates couldn't be uploaded
                    </h4>
                    <p className="text-muted mb-0">
                      We couldn't process these rates due to missing or invalid
                      data. Please check for missing or incorrect data.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  {/* <Button color="secondary" onClick={this.exportErrorCsv}>
                    <DownloadIcon size={16} className="mr-2" />
                    Export and fix CSV
                  </Button> */}
                
                  <span
                    onClick={this.toggleErrors}
                    role="button"
                    tabIndex={0}
                    aria-expanded={errorsOpen}
                    style={{
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: "18px",
                      transform: errorsOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 150ms ease",
                    }}
                    className="mr-2"
                  >
                    ▼
                  </span>
                </div>
              </div>
            )}

            {!confirmed && (
              <Collapse isOpen={errorsOpen} id="bulk-upload-errors-collapse">
                <div className="mb-5">
                  {this.state.stringErrors
                    ? this.renderStringErrors(this.state.stringErrors, this.state.originalRecords)
                    : this.renderTable(errorRecords, "error")}
                </div>
              </Collapse>
            )}

            {!confirmed && readyRecords.length > 0 && (
              <div>
                <div className="d-flex align-items-center mb-4">
                  <div
                    className="d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: "#e9fbf2",
                      color: "#11b56b",
                    }}
                  >
                    <CheckIcon size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1">
                      {readyRecords.length} exchange rates ready to be uploaded
                    </h4>
                    <p className="text-muted mb-0">
                      Review and edit the exchange rates before uploading them.
                    </p>
                  </div>
                </div>
                {this.renderTable(readyRecords, "ready")}
              </div>
            )}

            {confirmed && confirmedRecords && confirmedRecords.length > 0 && (
              <div className="mb-5">
                {this.renderSummaryTable(confirmedRecords)}
              </div>
            )}

            <div
              className="d-flex justify-content-end"
              style={{ position: "fixed", bottom: "2.2rem", right: "2.5rem" }}
            >
              {confirmed ? (
                <div className="d-flex">
                  <Button
                    color="secondary"
                    className="mr-3"
                    onClick={this.handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    className="btn-avi-primary"
                    onClick={this.handleUpload}
                  >
                    Upload exchange rate
                  </Button>
                </div>
              ) : (
                <div className="d-flex">
                  <Button
                    color="secondary"
                    className="mr-3"
                    onClick={function () {
                      this.props.history.push("/exchange-rate/bulk-upload");
                    }.bind(this)}
                  >
                    Back
                  </Button>
                  <Button
                    className="btn-avi-primary"
                    onClick={this.handleConfirm}
                    disabled={readyRecords.length === 0}
                  >
                    Confirm and continue
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </ViewContent>

        {/* ── Edit / Fix-error modal ── */}
        <Modal
          isOpen={editModalOpen}
          toggle={this.closeEditModal}
          className="modal-dialog-centered"
        >
          <ModalHeader toggle={this.closeEditModal}>
            Exchange rate details
          </ModalHeader>

          <ModalBody>
            {/* Error banner — only for error-mode records that still have issues */}
            {editMode === "error" && errorFieldCount > 0 && (
              <div
                className="mb-4 p-3"
                style={{
                  background: "#fff7ef",
                  borderRadius: 8,
                  borderLeft: "4px solid #ff7a1a",
                }}
              >
                <div
                  className="d-flex align-items-center mb-1"
                  style={{ color: "#ff7a1a", fontWeight: 600 }}
                >
                  <span className="mr-2" style={{ fontSize: 16 }}>
                    ⚠
                  </span>
                  Error detected ({errorFieldCount})
                </div>
                <p className="mb-2 text-muted" style={{ fontSize: 14 }}>
                  We discovered error(s) in this upload
                </p>
                <div style={{ fontSize: 13 }}>
                  <strong>Ways to fix this error</strong>
                  <ul className="mb-0 mt-1 pl-3">
                    <li>
                      Update your file with the correct parameters and try again
                    </li>
                    <li>Ensure details are entered correctly</li>
                  </ul>
                </div>
              </div>
            )}

            <Row>
              <Col sm="6">
                <FormGroup>
                  <Label>Country</Label>
                  <Input
                    name="Country"
                    value={editForm.Country}
                    onChange={this.handleEditChange}
                    style={
                      editFormErrors.Country ? { borderColor: "#dc3545" } : {}
                    }
                  />
                  {editFormErrors.Country && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: 12,
                        marginTop: 4,
                      }}
                    >
                      {editFormErrors.Country}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>Currency Code</Label>
                  <Input
                    name="CurrencyCode"
                    value={editForm.CurrencyCode}
                    onChange={this.handleEditChange}
                    style={
                      editFormErrors.CurrencyCode
                        ? { borderColor: "#dc3545" }
                        : {}
                    }
                  />
                  {editFormErrors.CurrencyCode && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: 12,
                        marginTop: 4,
                      }}
                    >
                      {editFormErrors.CurrencyCode}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>Exchange Rate (NGN)</Label>
                  <Input
                    type="number"
                    name="NGNExchangeRate"
                    value={editForm.NGNExchangeRate}
                    onChange={this.handleEditChange}
                    style={
                      editFormErrors.NGNExchangeRate
                        ? { borderColor: "#dc3545" }
                        : {}
                    }
                  />
                  {editFormErrors.NGNExchangeRate && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: 12,
                        marginTop: 4,
                      }}
                    >
                      {editFormErrors.NGNExchangeRate}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>Exchange Rate (USD)</Label>
                  <Input
                    type="number"
                    name="USDExchangeRate"
                    value={editForm.USDExchangeRate}
                    onChange={this.handleEditChange}
                    style={
                      editFormErrors.USDExchangeRate
                        ? { borderColor: "#dc3545" }
                        : {}
                    }
                  />
                  {editFormErrors.USDExchangeRate && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: 12,
                        marginTop: 4,
                      }}
                    >
                      {editFormErrors.USDExchangeRate}
                    </div>
                  )}
                  {/* {showUSDEquivalent && (
                    <FormText
                      style={{
                        backgroundColor: "#F3F3F3",
                        color: "#000000",
                        padding: "6px 8px",
                        display: "inline-block",
                        borderRadius: "4px",
                        width: "100%",
                        marginTop: "8px",
                      }}
                    >
                      USD Equivalent of $20 = {formattedUSDEq}
                    </FormText>
                  )} */}
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label>USD Equivalent of $20</Label>
                  <Input
                    type="number"
                    name="USD20_ROE"
                    value={editForm.USD20_ROE}
                    onChange={this.handleEditChange}
                    style={
                      editFormErrors.USD20_ROE ? { borderColor: "#dc3545" } : {}
                    }
                    disabled
                  />
                  {editFormErrors.USD20_ROE && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: 12,
                        marginTop: 4,
                      }}
                    >
                      {editFormErrors.USD20_ROE}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button className="btn-avi-primary" onClick={this.saveEdit}>
              {editMode === "error" ? "Update details" : "Save changes"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Success modal */}
        <Modal
          isOpen={successModalOpen}
          className="modal-dialog-centered"
          backdrop="static"
          keyboard={false}
        >
          <ModalBody>
            <div className="d-flex flex-column align-items-center text-center py-4 px-3">
              <div
                className="d-flex align-items-center justify-content-center mb-4"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "#e9fbf2",
                  color: "#11b56b",
                }}
              >
                <CheckIcon size={40} />
              </div>
              <h4 className="font-weight-bold mb-2">Exchange rates uploaded</h4>
              <p className="text-muted mb-4" style={{ maxWidth: 320 }}>
                You have successfully uploaded {totalRateSent} rates
              </p>
              <Button
                className="btn-avi-primary px-5"
                onClick={this.handleSuccessDone}
              >
                Done
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { requesting } = state.api || {};
  return { requesting };
}

export default connect(mapStateToProps)(BulkUploadExchangeRateDetails);



// WEBPACK FOOTER //
// ./src/views/exchange-rate/BulkUpload/bulk-upload-details.js